import {
  HexColor,
  hexToRgb,
  RgbColor,
  rgbToHex,
  stripAlpha,
} from '@teemill/utilities';

type ColorGradientStop = {
  position: number;
  color: RgbColor;
};

type LegacyColorGradientChannel = {
  stops: number[];
};

export default class ColorGradient {
  a?: LegacyColorGradientChannel;
  r?: LegacyColorGradientChannel;
  g?: LegacyColorGradientChannel;
  b?: LegacyColorGradientChannel;

  stops: ColorGradientStop[] = [];

  constructor(source?: ColorGradient) {
    if (source) {
      if (source.stops) {
        source.stops.forEach(({position, color}) => {
          this.addStop(position, color);
        });
      } else if (source.r && source.g && source.b && source.a) {
        // restore legacy gradients
        Object.keys(source.a.stops).forEach(index => {
          const numericIndex = parseFloat(index);
          this.addStop(numericIndex, {
            r: source.r?.stops[numericIndex] ?? 0,
            g: source.g?.stops[numericIndex] ?? 0,
            b: source.b?.stops[numericIndex] ?? 0,
            a: source.a?.stops[numericIndex] ?? 0,
          });
        });
      }
    }
  }

  public addStop(position: number, color: RgbColor) {
    if (typeof color === 'string') {
      color = hexToRgb(color);
    }

    if (typeof color.a === 'undefined') {
      color.a = 1;
    }

    this.stops.push({position, color});
  }

  public removeStopByPosition(position: number) {
    const stopIndex = this.stops.findIndex(stop => stop.position === position);

    if (stopIndex !== -1) {
      this.removeStop(stopIndex);
    }
  }

  public removeStop(index: number) {
    this.stops.splice(index, 1);
  }

  get stopsSortedByPosition(): ColorGradientStop[] {
    return [...this.stops].sort((a, b) => (a.position > b.position ? 1 : 0));
  }

  public getColorAtPosition(position: number): RgbColor {
    return this.interpolateColor(position);
  }

  public closestStopToPosition(
    position: number
  ): ColorGradientStop | undefined {
    const nextStop = this.nextStopFromPosition(position);
    const previousStop = this.previousStopFromPosition(position);

    if (!nextStop || !previousStop) {
      if (nextStop) {
        return nextStop;
      }

      if (previousStop) {
        return previousStop;
      }

      return undefined;
    }

    const distanceToNext = nextStop.position - position;
    const distanceToPrevious = previousStop.position - position;

    if (Math.abs(distanceToNext) <= Math.abs(distanceToPrevious)) {
      return nextStop;
    }

    return previousStop;
  }

  public nextStopFromPosition(position: number): ColorGradientStop | undefined {
    return this.stopsSortedByPosition.find(stop => stop.position > position);
  }

  public previousStopFromPosition(
    position: number
  ): ColorGradientStop | undefined {
    return this.stopsSortedByPosition
      .reverse()
      .find(stop => stop.position <= position);
  }

  private interpolateColor(position: number): RgbColor {
    const from = this.nextStopFromPosition(position);
    const to = this.previousStopFromPosition(position);

    if (!from || !to) {
      if (from) {
        return from.color;
      }

      if (to) {
        return to.color;
      }

      return {r: 0, g: 0, b: 0};
    }

    return {
      r: this.interpolateValues(
        from.color.r,
        to.color.r,
        from.position,
        to.position,
        position
      ),
      g: this.interpolateValues(
        from.color.g,
        to.color.g,
        from.position,
        to.position,
        position
      ),
      b: this.interpolateValues(
        from.color.b,
        to.color.b,
        from.position,
        to.position,
        position
      ),
      a: this.interpolateValues(
        from.color.a ?? 1,
        to.color.a ?? 1,
        from.position,
        to.position,
        position
      ),
    };
  }

  private interpolateValues(
    fromValue: number,
    toValue: number,
    fromPosition: number,
    toPosition: number,
    currentPosition: number
  ): number {
    const positionProgress = currentPosition - fromPosition;
    const distance = toPosition - fromPosition;

    return (
      fromValue + (toValue - fromValue) * (positionProgress * (1 / distance))
    );
  }

  public toCanvasGradient(canvasGradient: CanvasGradient): CanvasGradient {
    let previousStop: ColorGradientStop | null = null;

    const stops = this.stopsSortedByPosition;

    for (let i = 0; i < stops.length; ++i) {
      const stop = stops[i];

      let framePosition = stop.position;

      if (framePosition >= 0) {
        framePosition = Math.min(framePosition, 1);

        const colorValue = stripAlpha(
          rgbToHex(this.getColorAtPosition(framePosition))
        );
        canvasGradient.addColorStop(framePosition, colorValue);
        previousStop = stop;
      }
    }

    if (previousStop) {
      const colorValue = stripAlpha(
        rgbToHex(this.getColorAtPosition(previousStop.position))
      );
      canvasGradient.addColorStop(1, colorValue);
    }

    return canvasGradient;
  }
}
